/**
 * API Service - HTTP client for backend communication
 */
import axios from 'axios';
import type {
  Project,
  Pole,
  Cable,
  OpticalPath,
  CreateProjectRequest,
  CreatePoleRequest,
  CreateCableRequest,
  ValidationResult
} from '../types';
import type { Task, TaskStatus, TaskStats } from '../types/task';

const API_URL = process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== '' ? process.env.REACT_APP_API_URL : '/api';

console.log('[API] API_URL:', API_URL);
console.log('[API] REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add Authorization header to all requests
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage (where zustand persist stores it)
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
      try {
        const { state } = JSON.parse(authStorage);
        if (state?.token) {
          config.headers.Authorization = `Bearer ${state.token}`;
        }
      } catch (error) {
        console.error('Error parsing auth storage:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 Unauthorized responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't reload if this is a /auth/me request (checkAuth)
      // This allows the app to handle auth check failures gracefully
      const isAuthCheck = error.config?.url?.includes('/auth/me');

      if (!isAuthCheck) {
        // Clear auth storage and reload page to show login
        console.log('401 Unauthorized - clearing auth and reloading');
        localStorage.removeItem('auth-storage');
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// Projects API
export const projectsAPI = {
  getAll: () => api.get<Project[]>('/projects/'),
  getById: (id: number) => api.get<Project>(`/projects/${id}`),
  create: (data: CreateProjectRequest) => api.post<Project>('/projects/', data),
  update: (id: number, data: Partial<Project>) => api.put<Project>(`/projects/${id}`, data),
  delete: (id: number) => api.delete(`/projects/${id}`),
  restore: (id: number) => api.post<Project>(`/projects/${id}/restore`, {}),
  permanentDelete: (id: number) => api.delete(`/projects/${id}/permanent`),
  getDeleted: () => api.get<Project[]>('/projects/trash/list'),
  duplicate: (id: number, options?: any) => api.post<Project>(`/projects/${id}/duplicate`, options || {}),
  updateSettings: (id: number, settings: any) => api.patch<Project>(`/projects/${id}/settings`, settings),
  getFiberUsage: (id: number) => api.get<import('../types').ProjectFiberUsage>(`/projects/${id}/fiber-usage`),
  updateTopology2Layout: (id: number, layout: any) => api.patch(`/projects/${id}/topology2-layout`, layout),
  recalculateColors: (id: number) => api.post(`/projects/${id}/recalculate-colors`, {}),
};

// Poles API
export const polesAPI = {
  getAll: (projectId: number) => api.get<Pole[]>(`/projects/${projectId}/poles/`),
  getById: (projectId: number, poleId: number) => api.get<Pole>(`/projects/${projectId}/poles/${poleId}`),
  create: (projectId: number, data: CreatePoleRequest) => api.post<Pole>(`/projects/${projectId}/poles/`, data),
  update: (projectId: number, poleId: number, data: Partial<Pole>) =>
    api.put<import('../types').PoleUpdateResponse>(`/projects/${projectId}/poles/${poleId}`, data),
  delete: (projectId: number, poleId: number) => api.delete(`/projects/${projectId}/poles/${poleId}`),
  undo: (projectId: number, auditLogId: number) => api.post(`/projects/${projectId}/poles/undo/${auditLogId}`, {}),
  // Návin (rezerva kábla) na stĺpe — zadáva sa na stĺpe, ukladá po kábloch.
  getReserves: (projectId: number, poleId: number) =>
    api.get(`/projects/${projectId}/poles/${poleId}/reserves`),
  saveReserves: (projectId: number, poleId: number,
                 data: { items: { cable_id: number; meters: number }[] }) =>
    api.put(`/projects/${projectId}/poles/${poleId}/reserves`, data),
  // Hromadný import — jedno volanie za celý súbor. Predtým sa stĺpy vytvárali
  // po jednom vo slučke a chyba uprostred nechala import na polceste.
  importPoles: (projectId: number, data: {
    poles: { name: string; latitude: number; longitude: number; description?: string }[];
    default_pole_type: string;
    derive_type_from_name: boolean;
    skip_duplicates: boolean;
  }) => api.post(`/projects/${projectId}/poles/import`, data),

  // Virtual pole operations
  createCustomerPole: (projectId: number, poleId: number) =>
    api.post<Pole>(`/projects/${projectId}/poles/${poleId}/create-customer-pole`, {}),

  // Photo operations
  uploadPhoto: (projectId: number, poleId: number, formData: FormData) => {
    return api.post(`/projects/${projectId}/poles/${poleId}/photos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getPhotos: (projectId: number, poleId: number) =>
    api.get(`/projects/${projectId}/poles/${poleId}/photos`),
  deletePhoto: (projectId: number, poleId: number, photoId: string) =>
    api.delete(`/projects/${projectId}/poles/${poleId}/photos/${photoId}`),
  setPrimaryPhoto: (projectId: number, poleId: number, photoId: string) =>
    api.patch(`/projects/${projectId}/poles/${poleId}/photos/${photoId}/set-primary`),
  updatePhotoCaption: (projectId: number, poleId: number, photoId: string, caption: string) => {
    const formData = new FormData();
    formData.append('caption', caption);
    return api.patch(`/projects/${projectId}/poles/${poleId}/photos/${photoId}/caption`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Cables API
export const cablesAPI = {
  getAll: (projectId: number) => api.get<Cable[]>(`/projects/${projectId}/cables/`),
  getById: (projectId: number, cableId: number) => api.get<Cable>(`/projects/${projectId}/cables/${cableId}`),
  create: (projectId: number, data: CreateCableRequest) => api.post<Cable>(`/projects/${projectId}/cables/`, data),
  update: (projectId: number, cableId: number, data: Partial<Cable>) =>
    api.put<Cable>(`/projects/${projectId}/cables/${cableId}`, data),
  updateFibers: (projectId: number, cableId: number, fibers: any) =>
    api.patch<Cable>(`/projects/${projectId}/cables/${cableId}/fibers`, fibers),
  delete: (projectId: number, cableId: number) => api.delete(`/projects/${projectId}/cables/${cableId}`),
  getFiberUsage: (projectId: number, cableId: number) =>
    api.get<import('../types').CableFiberUsage>(`/projects/${projectId}/cables/${cableId}/fiber-usage`),
  // Uchytenie kábla na podporný stĺp — kábel sa neprerušuje, len vedie cezeň.
  attachSupportPole: (projectId: number, cableId: number, poleId: number) =>
    api.post(`/projects/${projectId}/cables/${cableId}/support-poles`, { pole_id: poleId }),
  detachSupportPole: (projectId: number, cableId: number, poleId: number) =>
    api.delete(`/projects/${projectId}/cables/${cableId}/support-poles/${poleId}`),
  // Zlúči dva káble, ktoré sa stretávajú na stĺpe, do jedného — stĺp sa tým
  // stane podporným. Ktorý kábel zostane, rozhoduje frontend (pozná optickú cestu).
  mergeCables: (projectId: number, data: { pole_id: number; keep_cable_id: number; remove_cable_id: number }) =>
    api.post(`/projects/${projectId}/cables/merge`, data),
  // Rozdelí kábel na podpornom stĺpe — keď tam pribudne krabica. Opak zlúčenia.
  splitCable: (projectId: number, cableId: number, data: { pole_id: number; keep_side: 'from' | 'to' }) =>
    api.post(`/projects/${projectId}/cables/${cableId}/split`, data),
};

// Calculations API
export const calculationsAPI = {
  calculate: (projectId: number) => api.post<OpticalPath[]>(`/projects/${projectId}/calculate/`),
  getPaths: (projectId: number) => api.get<OpticalPath[]>(`/projects/${projectId}/paths/`),
  validate: (projectId: number) => api.get<ValidationResult>(`/projects/${projectId}/validation/`),
};

// Export API
export const exportAPI = {
  exportPDF: (projectId: number) => api.get(`/projects/${projectId}/export/pdf/`, { responseType: 'blob' }),
  exportExcel: (projectId: number) => api.get(`/projects/${projectId}/export/excel/`, { responseType: 'blob' }),
  exportJSON: (projectId: number) => api.get(`/projects/${projectId}/export/json/`),
};

// Orders API (global, not project-specific)
export const ordersAPI = {
  getAll: (projectId?: number, assignedProjectId?: number, status?: string) => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    if (assignedProjectId) params.assigned_project_id = assignedProjectId;
    if (status) params.order_status = status;
    return api.get<import('../types').Order[]>(`/orders/`, { params });
  },
  getById: (orderId: number) =>
    api.get<import('../types').Order>(`/orders/${orderId}`),
  create: (data: import('../types').CreateOrderRequest) =>
    api.post<import('../types').Order>(`/orders/`, data),
  update: (orderId: number, data: import('../types').UpdateOrderRequest) =>
    api.put<import('../types').Order>(`/orders/${orderId}`, data),
  delete: (orderId: number) =>
    api.delete(`/orders/${orderId}`),
  approve: (orderId: number, assigned_project_id: number, internal_notes?: string) =>
    api.post<import('../types').Order>(`/orders/${orderId}/approve`, { assigned_project_id, internal_notes }),
  reject: (orderId: number, internal_notes: string) =>
    api.post<import('../types').Order>(`/orders/${orderId}/reject`, { internal_notes }),
  getStats: (projectId?: number, assignedProjectId?: number) => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    if (assignedProjectId) params.assigned_project_id = assignedProjectId;
    return api.get<import('../types').OrderStats>(`/orders/stats`, { params });
  },
};

// Customers API - customers are now global, not tied to projects
export const customersAPI = {
  // projectId zúži zoznam na jeden projekt; bez neho prídu zákazníci zo
  // všetkých projektov, na ktoré má používateľ právo (filtruje server).
  getAll: (status?: string, projectId?: number) => {
    const params: any = {};
    if (status) params.status = status;
    if (projectId) params.project_id = projectId;
    return api.get<import('../types').Customer[]>(`/customers/`, { params });
  },
  getById: (customerId: number) =>
    api.get<import('../types').Customer>(`/customers/${customerId}`),
  create: (data: import('../types').CreateCustomerRequest) =>
    api.post<import('../types').Customer>(`/customers/`, data),
  update: (customerId: number, data: import('../types').UpdateCustomerRequest) =>
    api.put<import('../types').Customer>(`/customers/${customerId}`, data),
  delete: (customerId: number) =>
    api.delete(`/customers/${customerId}`),
  activate: (customerId: number) =>
    api.post<import('../types').Customer>(`/customers/${customerId}/activate`),
  suspend: (customerId: number) =>
    api.post<import('../types').Customer>(`/customers/${customerId}/suspend`),
  terminate: (customerId: number) =>
    api.post<import('../types').Customer>(`/customers/${customerId}/terminate`),
  convertFromOrder: (orderId: number, data: import('../types').ConvertOrderToCustomerRequest) =>
    api.post<import('../types').Customer>(`/customers/convert-from-order/${orderId}`, data),
  getStats: (projectId?: number) => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    return api.get<import('../types').CustomerStats>(`/customers/stats`, { params });
  },
};

// Health check
export const healthCheck = () => api.get('/health');

// ONU API
export const onusAPI = {
  getAll: (projectId: number, params?: { status?: string; customer_id?: number; pole_id?: number }) =>
    api.get(`/projects/${projectId}/onus/`, { params }),
  getStats: (projectId: number) =>
    api.get(`/projects/${projectId}/onus/stats`),
  getById: (onuId: number) =>
    api.get(`/onus/${onuId}`),
  create: (data: any) =>
    api.post('/onus/', data),
  update: (onuId: number, data: any) =>
    api.put(`/onus/${onuId}`, data),
  delete: (onuId: number) =>
    api.delete(`/onus/${onuId}`),
  assignCustomer: (onuId: number, customerId: number) =>
    api.post(`/onus/${onuId}/assign-customer`, { customer_id: customerId }),
  unassignCustomer: (onuId: number) =>
    api.post(`/onus/${onuId}/unassign-customer`),
  getAvailablePorts: (poleId: number) =>
    api.get(`/poles/${poleId}/available-ports`),
};

// Tasks API
export const tasksAPI = {
  getProjectTasks: async (projectId: number, filters?: { status?: TaskStatus; task_type?: string; assigned_to?: string }): Promise<Task[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.task_type) params.append('task_type', filters.task_type);
    if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to);
    const response = await api.get(`/tasks/project/${projectId}?${params.toString()}`);
    return response.data;
  },
  getTask: async (taskId: number): Promise<Task> => {
    const response = await api.get(`/tasks/${taskId}`);
    return response.data;
  },
  createTask: async (task: Partial<Task>): Promise<Task> => {
    const response = await api.post('/tasks/', task);
    return response.data;
  },
  updateTask: async (taskId: number, updates: Partial<Task>): Promise<Task> => {
    const response = await api.put(`/tasks/${taskId}`, updates);
    return response.data;
  },
  updateTaskStatus: async (taskId: number, status: TaskStatus, notes?: string): Promise<Task> => {
    const response = await api.patch(`/tasks/${taskId}/status`, { status, notes });
    return response.data;
  },
  deleteTask: async (taskId: number): Promise<void> => {
    await api.delete(`/tasks/${taskId}`);
  },
  getTaskHistory: async (taskId: number): Promise<any[]> => {
    const response = await api.get(`/tasks/${taskId}/history`);
    return response.data;
  },
  getProjectStats: async (projectId: number): Promise<TaskStats> => {
    const response = await api.get(`/tasks/project/${projectId}/stats`);
    return response.data;
  },
  autoGenerateTasks: async (projectId: number): Promise<void> => {
    await api.post(`/tasks/project/${projectId}/auto-generate`);
  },
};

// Unified API export for stores
export const apiClient = {
  projects: projectsAPI,
  poles: polesAPI,
  cables: cablesAPI,
  calculations: calculationsAPI,
  export: exportAPI,
  orders: ordersAPI,
  onus: onusAPI,
  tasks: tasksAPI,
  healthCheck,
};

// Also export as 'api' for convenience
export { apiClient as api };

export default api;
